import { format } from "date-fns";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCustomArtStore } from "../../stores/custom-art-store";
import { useScheduleStore } from "../../stores/schedule-store";
import { exportScheduleArt } from "../preview-canvas/render-art";
import { PowerPointExportDialog } from "./PowerPointExportDialog";
import { PsdExportDialog } from "./PsdExportDialog";

export function SaveActions() {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [psdOpen, setPsdOpen] = useState(false);
  const [powerPointOpen, setPowerPointOpen] = useState(false);
  const details = useRef<HTMLDetailsElement>(null);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setFailed(false);
    const data = useScheduleStore.getState();
    try {
      await exportScheduleArt(
        data,
        `schedule-${format(data.week[0]!, "yyyy-MM-dd")}.webp`,
        useCustomArtStore.getState().layers,
        t("art.offline"),
      );
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative flex items-stretch">
      {failed && (
        <p
          role="alert"
          className="absolute right-0 top-full z-20 mt-2 w-64 rounded border border-red-200 bg-white p-3 text-xs text-red-600"
        >
          {t("art.exportError")}
        </p>
      )}
      <button
        className="flex h-8 items-center justify-center rounded-l bg-neutral-900 px-4 font-medium text-white hover:bg-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-neutral-900"
        type="button"
        onClick={save}
        disabled={saving}
        aria-busy={saving}
      >
        {t(saving ? "nav.saving" : "nav.save")}
      </button>
      <details
        ref={details}
        className="relative"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            event.currentTarget.open = false;
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.currentTarget.open = false;
            event.currentTarget.querySelector("summary")?.focus();
          }
        }}
      >
        <summary
          className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-r border-l border-neutral-700 bg-neutral-900 text-white hover:bg-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-neutral-900 [&::-webkit-details-marker]:hidden"
          aria-label={t("nav.additionalActions")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="m6 9 6 6 6-6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </summary>
        <div className="absolute right-0 top-full z-10 mt-2 w-48 rounded border border-neutral-200 bg-white p-1 shadow-lg">
          <button
            className="w-full rounded-sm p-2 text-left hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-neutral-900"
            type="button"
            onClick={() => {
              if (details.current) details.current.open = false;
              setPsdOpen(true);
            }}
          >
            {t("nav.savePsd")}
          </button>
          <button
            className="w-full rounded-sm p-2 text-left hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-neutral-900"
            type="button"
            onClick={() => {
              if (details.current) details.current.open = false;
              setPowerPointOpen(true);
            }}
          >
            {t("nav.savePowerPoint")}
          </button>
        </div>
      </details>
      <PsdExportDialog open={psdOpen} onOpenChange={setPsdOpen} />
      <PowerPointExportDialog open={powerPointOpen} onOpenChange={setPowerPointOpen} />
    </div>
  );
}
